/**
 * Connect a Domain Modal
 * 
 * Guided step-based domain connection flow for FundElite Starter White Label.
 * 
 * Steps:
 * 1. Choose Destination - What does the domain connect to?
 * 2. Enter Domain - Primary domain + optional redirect
 * 3. Setup Progress - Checking configuration
 * 4. Setup Method - Manual (default) or Automatic
 * 5. DNS Records - Manual DNS configuration with copy actions
 * 6. Success - Verified/Active state
 * 
 * Key Rules:
 * - Starter White Label = Client Portal destination only
 * - One Primary Client Portal Domain (required)
 * - One Optional Redirect Domain (redirect-only)
 * - Manual DNS setup is always available (default)
 * - Automatic setup only when provider support exists
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe,
  Link,
  Users,
  Mail,
  Code,
  Megaphone,
  ChevronRight,
  ChevronLeft,
  Check,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Copy,
  RefreshCw,
  Shield,
  Zap,
  Settings,
  Cloud,
  ExternalLink,
  Info,
  Lock,
  Crown,
  ArrowRight,
  X,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// ============================================
// DOMAIN DESTINATIONS
// ============================================

/**
 * Portal Types - Distinct customer-facing experiences
 * 
 * Managed Client Portal: For clients whose file is being worked by the agency
 * - Progress tracking, dispute status, document uploads, messages
 * - Guided, lower-control, progress/status-focused experience
 * 
 * Self-Serve / Auto Repair Portal: For end users actively using the product
 * - Report import, analysis, dispute wizard, credit builder, AI guidance
 * - Tool-driven, action-oriented experience
 */
const PORTAL_TYPES = {
  managed_client_portal: {
    id: 'managed_client_portal',
    name: 'Managed Client Portal',
    shortName: 'Managed Portal',
    description: 'For clients whose credit repair is managed by your agency. Includes progress tracking, dispute status, document uploads, and messages.',
    icon: Users,
    color: 'teal',
    starterWhiteLabel: true,
    example: 'portal.youragency.com',
    features: ['Progress tracking', 'Dispute status', 'Document uploads', 'Messages', 'Invoices'],
    userExperience: 'guided',
  },
  self_serve_portal: {
    id: 'self_serve_portal',
    name: 'Self-Serve / Auto Repair Portal',
    shortName: 'Self-Serve Portal',
    description: 'For customers who actively repair their credit themselves. Includes report import, AI analysis, dispute wizard, and credit tools.',
    icon: Zap,
    color: 'violet',
    starterWhiteLabel: true,
    example: 'app.youragency.com',
    features: ['Report import', 'AI analysis', 'Dispute wizard', 'Credit builder', 'Blueprints'],
    userExperience: 'self-service',
  },
};

/**
 * Additional domain destinations (non-portal)
 */
const ADDITIONAL_DESTINATIONS = {
  email_domain: {
    id: 'email_domain',
    name: 'Email Sending Domain',
    description: 'Send branded emails from your own domain for better deliverability.',
    icon: Mail,
    color: 'blue',
    starterWhiteLabel: false,
    example: 'mail.youragency.com',
  },
  marketing_site: {
    id: 'marketing_site',
    name: 'Marketing Site / Funnel',
    description: 'Connect your marketing pages and lead capture funnels.',
    icon: Megaphone,
    color: 'purple',
    starterWhiteLabel: false,
    example: 'www.youragency.com',
  },
  api_domain: {
    id: 'api_domain',
    name: 'API Domain',
    description: 'Custom domain for API integrations and webhooks.',
    icon: Code,
    color: 'amber',
    starterWhiteLabel: false,
    advancedOnly: true,
    example: 'api.youragency.com',
  },
};

// Combined destinations for backwards compatibility
const DOMAIN_DESTINATIONS = {
  ...PORTAL_TYPES,
  ...ADDITIONAL_DESTINATIONS,
};

// ============================================
// DNS PROVIDERS FOR AUTOMATIC SETUP
// ============================================

const DNS_PROVIDERS = [
  { id: 'cloudflare', name: 'Cloudflare', icon: Cloud, supported: true },
  { id: 'godaddy', name: 'GoDaddy', icon: Globe, supported: false },
  { id: 'namecheap', name: 'Namecheap', icon: Globe, supported: false },
  { id: 'route53', name: 'AWS Route 53', icon: Cloud, supported: false },
];

// Platform DNS target - MUST be configured for production
// This should be a brand-controlled hostname (e.g., edge.fundelitehq.com)
// NOT an internal preview/deployment hostname
//
// Set via: REACT_APP_PLATFORM_CNAME_TARGET environment variable
// Or fetched from backend API via getDNSRecords endpoint
const ENV_PLATFORM_CNAME_TARGET = process.env.REACT_APP_PLATFORM_CNAME_TARGET || '';

// Hook to get the platform CNAME target (from env or API)
const usePlatformTarget = (agencyId) => {
  const [target, setTarget] = useState(ENV_PLATFORM_CNAME_TARGET);
  const [loading, setLoading] = useState(!ENV_PLATFORM_CNAME_TARGET);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    // If we have it from env, use that
    if (ENV_PLATFORM_CNAME_TARGET) {
      setTarget(ENV_PLATFORM_CNAME_TARGET);
      setLoading(false);
      return;
    }

    // Otherwise fetch from API
    const fetchTarget = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/agencies/${agencyId}/dns-records`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.platform_target) {
          setTarget(response.data.platform_target);
        } else {
          setError('No platform CNAME target configured. Contact administrator.');
        }
      } catch (err) {
        setError('Failed to fetch DNS configuration');
      } finally {
        setLoading(false);
      }
    };

    if (agencyId && token) {
      fetchTarget();
    }
  }, [agencyId, token]);

  return { target, loading, error };
};

// ============================================
// DOMAIN DETECTION UTILITIES
// ============================================

/**
 * Common TLDs and second-level domain suffixes
 * Used to detect if a domain is a root/apex domain vs a subdomain
 */
const COMMON_TLDS = new Set([
  'com', 'net', 'org', 'io', 'co', 'ai', 'app', 'dev', 'xyz', 'info', 'biz',
  'us', 'uk', 'ca', 'au', 'de', 'fr', 'it', 'es', 'nl', 'be', 'ch', 'at',
  'edu', 'gov', 'mil', 'int', 'pro', 'name', 'mobi', 'asia', 'tel', 'jobs',
  'travel', 'museum', 'coop', 'aero', 'me', 'tv', 'cc', 'ws', 'fm', 'am',
  'online', 'store', 'shop', 'site', 'website', 'club', 'space', 'tech',
  'agency', 'consulting', 'solutions', 'services', 'digital', 'media',
]);

/**
 * Country-code second-level domains (e.g., .co.uk, .com.au)
 */
const CC_SLDS = new Set([
  'co.uk', 'org.uk', 'me.uk', 'ltd.uk', 'plc.uk',
  'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au',
  'co.nz', 'net.nz', 'org.nz',
  'co.za', 'org.za', 'net.za',
  'com.br', 'net.br', 'org.br',
  'co.in', 'net.in', 'org.in',
  'co.jp', 'or.jp', 'ne.jp',
  'com.mx', 'org.mx', 'net.mx',
  'com.ar', 'net.ar', 'org.ar',
]);

/**
 * Determines if a domain is a root/apex domain or a subdomain.
 * 
 * Root/apex domains: agency.com, mybrand.co, fundelite.ai
 * Subdomains: app.fundelite.ai, portal.agency.com, clients.brand.co
 * 
 * @param {string} domain - The domain to check
 * @returns {boolean} - true if it's a root/apex domain, false if it's a subdomain
 */
const isApexDomain = (domain) => {
  if (!domain) return false;
  
  // Clean the domain
  const cleanDomain = domain.toLowerCase().trim();
  
  // If it already starts with www., it's not eligible for www redirect
  if (cleanDomain.startsWith('www.')) return false;
  
  const parts = cleanDomain.split('.');
  
  // Need at least 2 parts (e.g., 'example.com')
  if (parts.length < 2) return false;
  
  // Check for country-code SLDs first (e.g., .co.uk, .com.au)
  if (parts.length >= 3) {
    const possibleCcSld = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
    if (CC_SLDS.has(possibleCcSld)) {
      // For CC-SLDs, apex is 3 parts: example.co.uk
      // Subdomain would be 4+ parts: app.example.co.uk
      return parts.length === 3;
    }
  }
  
  // Standard TLDs: apex is 2 parts (example.com), subdomain is 3+ parts (app.example.com)
  const tld = parts[parts.length - 1];
  if (COMMON_TLDS.has(tld) || tld.length <= 3) {
    return parts.length === 2;
  }
  
  // Default: if exactly 2 parts, treat as apex
  return parts.length === 2;
};

/**
 * Check if the www redirect option should be shown for a domain.
 * Only show for apex/root domains, never for subdomains.
 * 
 * @param {string} domain - The domain to check
 * @returns {boolean} - true if www redirect option should be shown
 */
const shouldShowWwwRedirectOption = (domain) => {
  if (!domain) return false;
  
  // Don't show if domain already starts with www
  if (domain.toLowerCase().startsWith('www.')) return false;
  
  // Only show for apex/root domains
  return isApexDomain(domain);
};

// ============================================
// HELPER COMPONENTS
// ============================================

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };
  
  return (
    <Button 
      variant="ghost" 
      size="sm" 
      onClick={handleCopy} 
      className="h-7 w-7 p-0"
      title={`Copy ${label}`}
    >
      {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
    </Button>
  );
};

const StepIndicator = ({ currentStep, totalSteps }) => {
  return (
    <div className="flex items-center gap-2 mb-6">
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
        <React.Fragment key={step}>
          <div 
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
              step < currentStep ? 'bg-emerald-500 text-white' :
              step === currentStep ? 'bg-teal-500 text-white' :
              'bg-slate-200 text-slate-500'
            }`}
          >
            {step < currentStep ? <Check size={16} /> : step}
          </div>
          {step < totalSteps && (
            <div className={`flex-1 h-1 rounded ${step < currentStep ? 'bg-emerald-500' : 'bg-slate-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ============================================
// STEP 1: CHOOSE DESTINATION
// ============================================

const DestinationStep = ({ 
  selectedDestination, 
  onSelectDestination, 
  onContinue,
  entitlements,
  isStarterWhiteLabel,
  agencyPortalTypes = { hasManagedPortal: true, hasSelfServePortal: true },
  existingBrandedPortal = null,
}) => {
  const { hasManagedPortal, hasSelfServePortal } = agencyPortalTypes;
  const hasBothPortalTypes = hasManagedPortal && hasSelfServePortal;
  
  const getAvailablePortalDestinations = () => {
    const portals = [];
    if (hasManagedPortal) portals.push(PORTAL_TYPES.managed_client_portal);
    if (hasSelfServePortal) portals.push(PORTAL_TYPES.self_serve_portal);
    return portals;
  };
  
  const portalDestinations = getAvailablePortalDestinations();
  
  const additionalDestinations = !isStarterWhiteLabel 
    ? Object.values(ADDITIONAL_DESTINATIONS).filter(dest => {
        if (dest.advancedOnly && !entitlements?.advanced_white_label) return false;
        return true;
      })
    : [];
  
  const isAlreadyBrandedPortal = (portalId) => isStarterWhiteLabel && existingBrandedPortal === portalId;
  const isPortalDisabledUnderStarter = (portalId) => {
    if (!isStarterWhiteLabel || !existingBrandedPortal) return false;
    return existingBrandedPortal !== portalId;
  };
  
  return (
    <div data-testid="connect-domain-step-1">
      <div className="text-center mb-6">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center mb-4">
          <Globe className="text-teal-600" size={28} />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">What do you want this domain to connect to?</h2>
        <p className="text-slate-500 text-sm max-w-md mx-auto">
          Choose where your custom domain will direct visitors.
        </p>
      </div>

      {isStarterWhiteLabel && hasBothPortalTypes && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="starter-limitation-notice">
          <div className="flex items-start gap-3">
            <Info className="text-amber-600 mt-0.5 flex-shrink-0" size={18} />
            <div>
              <p className="text-sm font-medium text-amber-900 mb-1">
                Starter White Label supports one primary branded customer-facing portal.
              </p>
              <p className="text-xs text-amber-700">
                Upgrade to Advanced White Label to brand multiple portal experiences with separate domains.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 px-1">
          Customer-Facing Portal
        </p>
        <div className="space-y-3">
          {portalDestinations.map((dest) => {
            const Icon = dest.icon;
            const isSelected = selectedDestination?.id === dest.id;
            const isDisabled = isPortalDisabledUnderStarter(dest.id);
            const isBranded = isAlreadyBrandedPortal(dest.id);
            
            return (
              <button
                key={dest.id}
                type="button"
                disabled={isDisabled}
                onClick={() => !isDisabled && onSelectDestination(dest)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  isDisabled ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                    : isSelected ? `border-${dest.color}-400 bg-${dest.color}-50 shadow-sm`
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                }`}
                data-testid={`destination-${dest.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    isDisabled ? 'bg-slate-100' : isSelected ? `bg-${dest.color}-100` : 'bg-slate-100'
                  }`}>
                    <Icon size={22} className={isDisabled ? 'text-slate-400' : isSelected ? `text-${dest.color}-600` : 'text-slate-500'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className={`font-semibold ${isDisabled ? 'text-slate-500' : 'text-slate-900'}`}>{dest.name}</h4>
                      {isSelected && <CheckCircle size={16} className="text-emerald-500" />}
                      {isBranded && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">Branded</Badge>}
                      {dest.starterWhiteLabel && !isBranded && !isDisabled && (
                        <Badge className="bg-teal-100 text-teal-700 border-0 text-xs">Starter</Badge>
                      )}
                      {isDisabled && <Badge className="bg-slate-100 text-slate-500 border-0 text-xs">Standard Hosted</Badge>}
                    </div>
                    <p className={`text-sm leading-relaxed ${isDisabled ? 'text-slate-400' : 'text-slate-500'}`}>{dest.description}</p>
                    <p className="text-xs text-slate-400 mt-1 font-mono">e.g., {dest.example}</p>
                    {dest.features && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {dest.features.slice(0, 4).map((feature, idx) => (
                          <span key={idx} className={`text-xs px-2 py-0.5 rounded-full ${isDisabled ? 'bg-slate-100 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
                            {feature}
                          </span>
                        ))}
                        {dest.features.length > 4 && <span className="text-xs text-slate-400">+{dest.features.length - 4} more</span>}
                      </div>
                    )}
                    {isDisabled && (
                      <div className="mt-2 p-2 bg-slate-100 rounded-lg">
                        <p className="text-xs text-slate-500">
                          <strong>Standard Hosted Portal:</strong> Remains accessible at FundElite's default domain. Your agency name and logo still appear, but no custom domain.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      
      {!isStarterWhiteLabel && additionalDestinations.length > 0 && (
        <div className="pt-4 border-t border-slate-200 mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 px-1">Additional Surfaces</p>
          <div className="space-y-3">
            {additionalDestinations.map((dest) => {
              const Icon = dest.icon;
              const isSelected = selectedDestination?.id === dest.id;
              return (
                <button
                  key={dest.id}
                  type="button"
                  onClick={() => onSelectDestination(dest)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    isSelected ? `border-${dest.color}-400 bg-${dest.color}-50 shadow-sm` : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                  data-testid={`destination-${dest.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isSelected ? `bg-${dest.color}-100` : 'bg-slate-100'}`}>
                      <Icon size={22} className={isSelected ? `text-${dest.color}-600` : 'text-slate-500'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-slate-900">{dest.name}</h4>
                        {isSelected && <CheckCircle size={16} className="text-emerald-500" />}
                        <Badge className="bg-purple-100 text-purple-700 border-0 text-xs">Advanced</Badge>
                      </div>
                      <p className="text-sm text-slate-500 leading-relaxed">{dest.description}</p>
                      <p className="text-xs text-slate-400 mt-1 font-mono">e.g., {dest.example}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
        
      {isStarterWhiteLabel && (
        <div className="pt-4 border-t border-slate-200">
          <p className="text-xs text-slate-500 mb-3 px-1">Additional destinations available with Advanced White Label:</p>
          <div className="space-y-2">
            {Object.values(ADDITIONAL_DESTINATIONS).map((dest) => {
              const Icon = dest.icon;
              return (
                <div key={dest.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200 opacity-60">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Icon size={18} className="text-slate-400" />
                  </div>
                  <div className="flex-1"><p className="text-sm font-medium text-slate-500">{dest.name}</p></div>
                  <Lock size={14} className="text-slate-400" />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex justify-end mt-6">
        <Button 
          onClick={onContinue}
          disabled={!selectedDestination}
          className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white"
          data-testid="continue-step-1"
        >
          Continue
          <ChevronRight size={16} className="ml-1" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// STEP 2: ENTER DOMAIN
// ============================================

const EnterDomainStep = ({ 
  destination,
  primaryDomain, 
  setPrimaryDomain, 
  redirectDomain,
  setRedirectDomain,
  includeWwwRedirect,
  setIncludeWwwRedirect,
  onBack, 
  onContinue,
}) => {
  const [errors, setErrors] = useState({});
  
  const validateDomain = (domain) => {
    if (!domain) return false;
    // Basic domain validation
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z]{2,})+$/;
    return domainRegex.test(domain);
  };
  
  const handleContinue = () => {
    const newErrors = {};
    if (!primaryDomain?.trim()) {
      newErrors.primary = 'Domain is required';
    } else if (!validateDomain(primaryDomain)) {
      newErrors.primary = 'Please enter a valid domain (e.g., portal.youragency.com)';
    }
    
    if (redirectDomain && !validateDomain(redirectDomain)) {
      newErrors.redirect = 'Please enter a valid domain';
    }
    
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      onContinue();
    }
  };
  
  return (
    <div data-testid="connect-domain-step-2">
      <div className="text-center mb-6">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center mb-4">
          <Link className="text-teal-600" size={28} />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">Enter Your Domain</h2>
        <p className="text-slate-500 text-sm max-w-md mx-auto">
          Connect your domain to the <strong>{destination?.name}</strong>.
        </p>
      </div>

      <div className="space-y-5">
        {/* Primary Domain */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="text-teal-600" size={18} />
            <Label className="font-semibold text-slate-900">Domain URL</Label>
            <Badge variant="outline" className="text-xs text-slate-600 border-slate-300">Required</Badge>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">https://</span>
            <Input
              value={primaryDomain}
              onChange={(e) => {
                setPrimaryDomain(e.target.value.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, ''));
                if (errors.primary) setErrors({ ...errors, primary: null });
              }}
              placeholder={destination?.example || 'portal.youragency.com'}
              className={`bg-white font-mono flex-1 ${errors.primary ? 'border-red-300' : ''}`}
              data-testid="input-primary-domain"
            />
          </div>
          {errors.primary && (
            <p className="text-xs text-red-500 mt-2">{errors.primary}</p>
          )}
          <p className="text-xs text-slate-500 mt-2">
            Enter the domain you want your clients to use to access their portal.
          </p>
        </div>
        
        {/* WWW Redirect Option - Only show for apex/root domains */}
        {shouldShowWwwRedirectOption(primaryDomain) && (
          <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200" data-testid="www-redirect-option">
            <Checkbox
              id="www_redirect"
              checked={includeWwwRedirect}
              onCheckedChange={setIncludeWwwRedirect}
              data-testid="checkbox-www-redirect"
            />
            <div className="flex-1">
              <Label htmlFor="www_redirect" className="text-sm font-medium text-slate-900 cursor-pointer">
                Also add www.{primaryDomain} and redirect to {primaryDomain}
              </Label>
              <p className="text-xs text-slate-500 mt-1">
                Visitors to www.{primaryDomain} will be automatically redirected.
              </p>
            </div>
          </div>
        )}
        
        {/* Optional Redirect Domain */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <ArrowRight className="text-blue-500" size={18} />
            <Label className="font-semibold text-slate-900">Optional Redirect Domain</Label>
            <Badge variant="outline" className="text-xs text-slate-600 border-slate-300">Optional</Badge>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">https://</span>
            <Input
              value={redirectDomain}
              onChange={(e) => {
                setRedirectDomain(e.target.value.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, ''));
                if (errors.redirect) setErrors({ ...errors, redirect: null });
              }}
              placeholder="clients.youragency.com"
              className={`bg-white font-mono flex-1 ${errors.redirect ? 'border-red-300' : ''}`}
              data-testid="input-redirect-domain"
            />
          </div>
          {errors.redirect && (
            <p className="text-xs text-red-500 mt-2">{errors.redirect}</p>
          )}
          
          {redirectDomain && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3 flex items-start gap-2">
              <Info size={14} className="text-blue-600 mt-0.5" />
              <p className="text-xs text-blue-800">
                <strong>Redirect Only:</strong> Visitors to {redirectDomain} will be 
                automatically redirected to {primaryDomain || 'your primary domain'}.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-between">
        <Button variant="outline" onClick={onBack} data-testid="back-step-2">
          <ChevronLeft size={16} className="mr-1" />
          Back
        </Button>
        <Button 
          onClick={handleContinue}
          className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white"
          data-testid="continue-step-2"
        >
          Continue
          <ChevronRight size={16} className="ml-1" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// STEP 3: SETUP PROGRESS
// ============================================

const SetupProgressStep = ({ primaryDomain, onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState('');
  
  const tasks = [
    { label: 'Validating domain...', duration: 800 },
    { label: 'Generating DNS records...', duration: 600 },
    { label: 'Checking provider support...', duration: 500 },
    { label: 'Preparing verification steps...', duration: 400 },
  ];
  
  useEffect(() => {
    let taskIndex = 0;
    let totalTime = 0;
    
    const runTasks = () => {
      if (taskIndex < tasks.length) {
        setCurrentTask(tasks[taskIndex].label);
        setProgress(((taskIndex + 1) / tasks.length) * 100);
        
        setTimeout(() => {
          taskIndex++;
          totalTime += tasks[taskIndex - 1].duration;
          runTasks();
        }, tasks[taskIndex].duration);
      } else {
        setTimeout(() => onComplete(), 300);
      }
    };
    
    runTasks();
  }, []);
  
  return (
    <div className="text-center py-8" data-testid="connect-domain-step-3">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center mb-6">
        <Loader2 className="text-teal-600 animate-spin" size={32} />
      </div>
      
      <h2 className="text-xl font-bold text-slate-900 mb-2">Setting Up Your Domain</h2>
      <p className="text-slate-500 text-sm mb-6">{primaryDomain}</p>
      
      {/* Progress Bar */}
      <div className="w-full max-w-xs mx-auto mb-4">
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-teal-400 to-teal-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      
      <p className="text-sm text-slate-600">{currentTask}</p>
    </div>
  );
};

// ============================================
// STEP 4: SETUP METHOD
// ============================================

const SetupMethodStep = ({ 
  setupMethod, 
  setSetupMethod, 
  selectedProvider,
  setSelectedProvider,
  onBack, 
  onContinue,
}) => {
  const supportedProviders = DNS_PROVIDERS.filter(p => p.supported);
  const hasAutoSupport = supportedProviders.length > 0;
  
  return (
    <div data-testid="connect-domain-step-4">
      <div className="text-center mb-6">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center mb-4">
          <Settings className="text-teal-600" size={28} />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">Choose Setup Method</h2>
        <p className="text-slate-500 text-sm max-w-md mx-auto">
          Select how you'd like to connect your domain.
        </p>
      </div>

      <div className="space-y-4 mb-6">
        {/* Manual Setup Option */}
        <button
          onClick={() => setSetupMethod('manual')}
          className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
            setupMethod === 'manual'
              ? 'border-teal-400 bg-teal-50 shadow-sm'
              : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
          data-testid="setup-method-manual"
        >
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
              setupMethod === 'manual' ? 'bg-teal-100' : 'bg-slate-100'
            }`}>
              <Settings size={22} className={setupMethod === 'manual' ? 'text-teal-600' : 'text-slate-500'} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-slate-900">Set Up Manually</h4>
                <Badge className="bg-teal-100 text-teal-700 border-0 text-xs">Recommended</Badge>
                {setupMethod === 'manual' && <CheckCircle size={16} className="text-emerald-500 ml-auto" />}
              </div>
              <p className="text-sm text-slate-500">
                Add DNS records to your domain provider. Works with any registrar.
              </p>
            </div>
          </div>
        </button>
        
        {/* Automatic Setup Option - Only show if supported */}
        {hasAutoSupport && (
          <button
            onClick={() => setSetupMethod('automatic')}
            className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
              setupMethod === 'automatic'
                ? 'border-teal-400 bg-teal-50 shadow-sm'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
            data-testid="setup-method-automatic"
          >
            <div className="flex items-start gap-4">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                setupMethod === 'automatic' ? 'bg-teal-100' : 'bg-slate-100'
              }`}>
                <Zap size={22} className={setupMethod === 'automatic' ? 'text-teal-600' : 'text-slate-500'} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-slate-900">Connect Automatically</h4>
                  {setupMethod === 'automatic' && <CheckCircle size={16} className="text-emerald-500 ml-auto" />}
                </div>
                <p className="text-sm text-slate-500">
                  Connect your DNS provider and we'll configure everything for you.
                </p>
              </div>
            </div>
          </button>
        )}
      </div>
      
      {/* Provider Selection for Automatic */}
      {setupMethod === 'automatic' && hasAutoSupport && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 mb-6">
          <h4 className="font-semibold text-slate-900 mb-3">Select Your DNS Provider</h4>
          <div className="grid grid-cols-2 gap-3">
            {supportedProviders.map((provider) => {
              const Icon = provider.icon;
              const isSelected = selectedProvider === provider.id;
              
              return (
                <button
                  key={provider.id}
                  onClick={() => setSelectedProvider(provider.id)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    isSelected 
                      ? 'border-teal-400 bg-teal-50' 
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  data-testid={`provider-${provider.id}`}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={18} className={isSelected ? 'text-teal-600' : 'text-slate-500'} />
                    <span className={`font-medium ${isSelected ? 'text-teal-900' : 'text-slate-700'}`}>
                      {provider.name}
                    </span>
                    {isSelected && <CheckCircle size={14} className="text-teal-500 ml-auto" />}
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Coming Soon Providers */}
          {DNS_PROVIDERS.filter(p => !p.supported).length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 mb-2">Coming soon:</p>
              <div className="flex flex-wrap gap-2">
                {DNS_PROVIDERS.filter(p => !p.supported).map(p => (
                  <Badge key={p.id} variant="outline" className="text-slate-400 text-xs">
                    {p.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} data-testid="back-step-4">
          <ChevronLeft size={16} className="mr-1" />
          Back
        </Button>
        <Button 
          onClick={onContinue}
          disabled={!setupMethod || (setupMethod === 'automatic' && !selectedProvider)}
          className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white"
          data-testid="continue-step-4"
        >
          Continue
          <ChevronRight size={16} className="ml-1" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// STEP 5: DNS RECORDS
// ============================================

const DNSRecordsStep = ({ 
  primaryDomain, 
  redirectDomain,
  includeWwwRedirect,
  agencyId,
  destination,
  onBack, 
  onVerify,
  isVerifying,
  verificationResult,
  platformTarget, // Now passed from parent
}) => {
  const records = [];
  
  // Check if platform target is configured
  if (!platformTarget) {
    return (
      <div data-testid="connect-domain-step-5">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center mb-4">
            <AlertCircle className="text-amber-600" size={28} />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Configuration Required</h2>
          <p className="text-slate-500 text-sm max-w-md mx-auto mb-4">
            The platform CNAME target is not configured. This must be set up before custom domains can be connected.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left text-sm">
            <p className="font-medium text-amber-800 mb-2">Administrator Action Required:</p>
            <ol className="list-decimal list-inside text-amber-700 space-y-1">
              <li>Create a branded hostname (e.g., <code className="bg-amber-100 px-1 rounded">edge.fundelitehq.com</code>)</li>
              <li>Configure it in Cloudflare to proxy to your app</li>
              <li>Set <code className="bg-amber-100 px-1 rounded">PLATFORM_CNAME_TARGET</code> environment variable</li>
            </ol>
          </div>
        </div>
        <div className="flex justify-start">
          <Button variant="outline" onClick={onBack} data-testid="back-step-5">
            <ChevronLeft size={16} className="mr-1" />
            Back
          </Button>
        </div>
      </div>
    );
  }
  
  // Primary domain records
  if (primaryDomain) {
    records.push({
      group: primaryDomain,
      items: [
        { type: 'CNAME', host: primaryDomain, value: platformTarget },
        { type: 'TXT', host: `_verify.${primaryDomain}`, value: `fundelite-verify=${agencyId}` },
      ]
    });
  }
  
  // WWW redirect records - Only include if domain is apex and www redirect is enabled
  if (includeWwwRedirect && shouldShowWwwRedirectOption(primaryDomain)) {
    records.push({
      group: `www.${primaryDomain}`,
      items: [
        { type: 'CNAME', host: `www.${primaryDomain}`, value: platformTarget },
      ]
    });
  }
  
  // Redirect domain records
  if (redirectDomain) {
    records.push({
      group: redirectDomain,
      items: [
        { type: 'CNAME', host: redirectDomain, value: platformTarget },
      ]
    });
  }
  
  return (
    <div data-testid="connect-domain-step-5">
      <div className="text-center mb-6">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center mb-4">
          <Settings className="text-teal-600" size={28} />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">Add DNS Records</h2>
        <p className="text-slate-500 text-sm max-w-md mx-auto">
          Add these records to your domain provider to connect your domain.
        </p>
      </div>

      <div className="space-y-4 mb-6">
        {records.map((group, groupIdx) => (
          <div key={groupIdx} className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 bg-slate-100 border-b border-slate-200">
              <p className="text-sm font-semibold text-slate-700">For {group.group}</p>
            </div>
            
            {/* Headers - Improved proportions for wider modal */}
            <div className="grid grid-cols-12 gap-3 px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <div className="col-span-1">Type</div>
              <div className="col-span-4">Host</div>
              <div className="col-span-6">Required Value</div>
              <div className="col-span-1 text-center">Status</div>
            </div>
            
            {/* Records - Better spacing and larger code blocks */}
            {group.items.map((record, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-3 px-5 py-3.5 items-center border-b border-slate-100 last:border-0">
                <div className="col-span-1">
                  <Badge variant="outline" className="font-mono text-xs px-2 py-0.5">
                    {record.type}
                  </Badge>
                </div>
                <div className="col-span-4">
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-white px-3 py-1.5 rounded-md border border-slate-200 font-mono text-slate-800 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                      {record.host}
                    </code>
                    <CopyButton value={record.host} label="Host" />
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-white px-3 py-1.5 rounded-md border border-slate-200 font-mono text-slate-800 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                      {record.value}
                    </code>
                    <CopyButton value={record.value} label="Value" />
                  </div>
                </div>
                <div className="col-span-1 text-center">
                  {verificationResult?.verified ? (
                    <CheckCircle size={18} className="text-emerald-500 inline" />
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      
      {/* Propagation Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
        <div className="flex items-start gap-4">
          <AlertCircle className="text-amber-500 mt-0.5 flex-shrink-0" size={20} />
          <div>
            <h4 className="font-semibold text-amber-900 text-sm mb-1">DNS Propagation Time</h4>
            <p className="text-sm text-amber-800 leading-relaxed">
              DNS changes typically take 5-30 minutes to propagate, but can take up to 48 hours in some cases.
              You can verify your records at any time.
            </p>
          </div>
        </div>
      </div>
      
      {/* Verification Result */}
      {verificationResult && !verificationResult.verified && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6">
          <div className="flex items-start gap-4">
            <XCircle className="text-red-500 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <h4 className="font-semibold text-red-900 text-sm mb-1">Verification Pending</h4>
              <p className="text-sm text-red-800 leading-relaxed">
                {verificationResult.message || 'DNS records not yet detected. Please check your DNS settings and try again.'}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onBack} data-testid="back-step-5">
          <ChevronLeft size={16} className="mr-1" />
          Back
        </Button>
        <Button 
          onClick={onVerify}
          disabled={isVerifying}
          className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white px-6"
          data-testid="verify-records-btn"
        >
          {isVerifying ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Verifying...
            </>
          ) : (
            <>
              <RefreshCw size={16} className="mr-2" />
              Verify Records
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// ============================================
// STEP 6: SUCCESS
// ============================================

const SuccessStep = ({ 
  primaryDomain, 
  redirectDomain,
  includeWwwRedirect,
  destination,
  sslStatus,
  onClose,
  onManageDomains,
}) => {
  return (
    <div className="text-center py-6" data-testid="connect-domain-step-6">
      <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
        <CheckCircle className="text-emerald-600" size={36} />
      </div>
      
      <h2 className="text-xl font-bold text-slate-900 mb-2">Domain Connected!</h2>
      <p className="text-slate-500 mb-6 max-w-md mx-auto">
        Your custom domain is now active and ready to use.
      </p>

      <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5 text-left mb-6 max-w-md mx-auto">
        <h4 className="font-semibold text-emerald-900 mb-4">Connection Summary</h4>
        
        <div className="space-y-3">
          {/* Primary Domain */}
          <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-200">
            <div className="flex items-center gap-3">
              <Globe className="text-teal-600" size={18} />
              <div>
                <code className="text-sm font-mono font-medium text-slate-900">{primaryDomain}</code>
                <p className="text-xs text-slate-500">{destination?.name}</p>
              </div>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 border-0">Active</Badge>
          </div>
          
          {/* WWW Redirect - Only show for apex domains */}
          {includeWwwRedirect && shouldShowWwwRedirectOption(primaryDomain) && (
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-200">
              <div className="flex items-center gap-3">
                <ArrowRight className="text-blue-500" size={18} />
                <div>
                  <code className="text-sm font-mono text-slate-700">www.{primaryDomain}</code>
                  <p className="text-xs text-slate-500">Redirect</p>
                </div>
              </div>
              <Badge className="bg-blue-100 text-blue-700 border-0">Redirect</Badge>
            </div>
          )}
          
          {/* Redirect Domain */}
          {redirectDomain && (
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-200">
              <div className="flex items-center gap-3">
                <ArrowRight className="text-blue-500" size={18} />
                <div>
                  <code className="text-sm font-mono text-slate-700">{redirectDomain}</code>
                  <p className="text-xs text-slate-500">Redirect</p>
                </div>
              </div>
              <Badge className="bg-blue-100 text-blue-700 border-0">Redirect</Badge>
            </div>
          )}
          
          {/* SSL Status */}
          <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-200">
            <div className="flex items-center gap-3">
              <Shield className="text-emerald-600" size={18} />
              <div>
                <p className="text-sm font-medium text-slate-900">SSL Certificate</p>
                <p className="text-xs text-slate-500">HTTPS encryption</p>
              </div>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 border-0">
              {sslStatus === 'active' ? 'Active' : 'Provisioning'}
            </Badge>
          </div>
        </div>
      </div>
      
      {/* Portal Link */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 mb-6 max-w-md mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ExternalLink className="text-slate-500" size={16} />
            <span className="text-sm font-medium text-slate-700">Your Portal URL</span>
          </div>
          <a 
            href={`https://${primaryDomain}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-teal-600 hover:underline font-mono"
          >
            https://{primaryDomain}
          </a>
        </div>
      </div>

      <div className="flex gap-3 justify-center">
        <Button variant="outline" onClick={onClose} data-testid="close-success-btn">
          Close
        </Button>
        <Button 
          onClick={onManageDomains}
          className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white"
          data-testid="manage-domains-btn"
        >
          Manage Domains
        </Button>
      </div>
    </div>
  );
};

// ============================================
// MAIN MODAL COMPONENT
// ============================================

const ConnectDomainModal = ({ 
  isOpen, 
  onClose, 
  agencyId,
  entitlements,
  isStarterWhiteLabel = true,
  onSuccess,
  // Agency portal configuration
  agencyPortalTypes = { hasManagedPortal: true, hasSelfServePortal: true },
  // Existing branded portal ID (for Starter limitation)
  existingBrandedPortal = null,
}) => {
  const { token } = useAuth();
  
  // Get platform CNAME target
  const { target: platformTarget, loading: targetLoading, error: targetError } = usePlatformTarget(agencyId);
  
  // Step state
  const [step, setStep] = useState(1);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  
  // Form data
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [primaryDomain, setPrimaryDomain] = useState('');
  const [redirectDomain, setRedirectDomain] = useState('');
  const [includeWwwRedirect, setIncludeWwwRedirect] = useState(true);
  const [setupMethod, setSetupMethod] = useState('manual');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [sslStatus, setSslStatus] = useState('pending');
  
  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedDestination(null);
      setPrimaryDomain('');
      setRedirectDomain('');
      setIncludeWwwRedirect(true);
      setSetupMethod('manual');
      setSelectedProvider(null);
      setVerificationResult(null);
      setSslStatus('pending');
    }
  }, [isOpen]);
  
  const handleProgressComplete = () => {
    setStep(4);
  };
  
  const handleVerify = async () => {
    setIsVerifying(true);
    setVerificationResult(null);
    
    try {
      const response = await axios.post(
        `${API}/admin/agencies/${agencyId}/domains/verify`,
        { 
          primary_domain: primaryDomain,
          redirect_domain: redirectDomain || (includeWwwRedirect && shouldShowWwwRedirectOption(primaryDomain) ? `www.${primaryDomain}` : ''),
          branded_portal_type: selectedDestination?.id || 'managed_client_portal', // Send selected portal type
        },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      
      const result = response.data;
      
      if (result.primary_verified) {
        setSslStatus(result.ssl_status || 'provisioning');
        setStep(6); // Success
        toast.success('Domain connected successfully!');
        if (onSuccess) onSuccess(result);
      } else {
        setVerificationResult({
          verified: false,
          message: result.message || 'DNS records not yet detected. Please check your settings.',
        });
        toast.info('Verification pending. DNS may still be propagating.');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setVerificationResult({
        verified: false,
        message: err.response?.data?.detail || 'Verification failed. Please try again.',
      });
      toast.error('Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };
  
  const handleClose = () => {
    onClose();
  };
  
  const getTotalSteps = () => {
    // Steps: 1-Destination, 2-Domain, 3-Progress, 4-Method, 5-DNS, 6-Success
    return 6;
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="w-[min(1160px,90vw)] max-w-none max-h-[90vh] overflow-y-auto" 
        data-testid="connect-domain-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe size={20} className="text-teal-600" />
            Connect a Domain
          </DialogTitle>
        </DialogHeader>

        {step !== 3 && step !== 6 && (
          <StepIndicator currentStep={step} totalSteps={getTotalSteps()} />
        )}

        {step === 1 && (
          <DestinationStep
            selectedDestination={selectedDestination}
            onSelectDestination={setSelectedDestination}
            onContinue={() => setStep(2)}
            entitlements={entitlements}
            isStarterWhiteLabel={isStarterWhiteLabel}
            agencyPortalTypes={agencyPortalTypes}
            existingBrandedPortal={existingBrandedPortal}
          />
        )}
        
        {step === 2 && (
          <EnterDomainStep
            destination={selectedDestination}
            primaryDomain={primaryDomain}
            setPrimaryDomain={setPrimaryDomain}
            redirectDomain={redirectDomain}
            setRedirectDomain={setRedirectDomain}
            includeWwwRedirect={includeWwwRedirect}
            setIncludeWwwRedirect={setIncludeWwwRedirect}
            onBack={() => setStep(1)}
            onContinue={() => setStep(3)}
          />
        )}
        
        {step === 3 && (
          <SetupProgressStep
            primaryDomain={primaryDomain}
            onComplete={handleProgressComplete}
          />
        )}
        
        {step === 4 && (
          <SetupMethodStep
            setupMethod={setupMethod}
            setSetupMethod={setSetupMethod}
            selectedProvider={selectedProvider}
            setSelectedProvider={setSelectedProvider}
            onBack={() => setStep(2)}
            onContinue={() => setStep(5)}
          />
        )}
        
        {step === 5 && (
          <DNSRecordsStep
            primaryDomain={primaryDomain}
            redirectDomain={redirectDomain}
            includeWwwRedirect={includeWwwRedirect}
            agencyId={agencyId}
            destination={selectedDestination}
            onBack={() => setStep(4)}
            onVerify={handleVerify}
            isVerifying={isVerifying}
            verificationResult={verificationResult}
            platformTarget={platformTarget}
          />
        )}
        
        {step === 6 && (
          <SuccessStep
            primaryDomain={primaryDomain}
            redirectDomain={redirectDomain}
            includeWwwRedirect={includeWwwRedirect}
            destination={selectedDestination}
            sslStatus={sslStatus}
            onClose={handleClose}
            onManageDomains={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConnectDomainModal;
